/**
 * Funnels pre-built from a site's own traffic, shown as rows in the Funnels
 * list before anything is saved (Paper "Funnels · List · 1440").
 *
 * Built from the suggestions endpoint (`GET properties/:id/funnels/suggestions`:
 * top pages, clicks and form submissions in the window). No AI: each funnel
 * is a short path from where visitors land to something that shows intent
 * (a pricing / contact / booking page, a form submit, a CTA click). Short on
 * purpose: chaining the four most-visited pages (the old "Top Pages Journey")
 * measures nothing anyone acts on.
 *
 * Output is the saved-funnel shape, so `useSavedFunnelResults` runs it and
 * the studio opens it like any saved funnel.
 */
import type { FunnelSuggestionsResponse, EventSuggestion, PageSuggestion } from "@/lib/types/funnel-api"

export type SuggestedFunnelStep = {
  name: string
  url: string
  type: "page_view" | "click" | "form_submission"
  secondary?: string
}

export type SuggestedFunnel = {
  id: string
  name: string
  steps: SuggestedFunnelStep[]
}

const INTENT_PAGE =
  /pricing|plans?\b|work-with-me|hire|contact|demo|book|consult|services|sign-?up|register|get-started|trial|apply|checkout|cart|subscribe|join|quote|start/i
const CONTENT_PAGE = /^\/(blog|articles?|posts?|news|resources|guides?|learn)(\/|$)/i
const CHROME_CLICK = /(^|[-_\s])(nav|navbar|footer|logo|menu|toggle|mobilenav|breadcrumb|skip)([-_\s]|$)|^#/i
const GENERIC_TARGET = /^(link|a|button|div|span|img|svg)$/i
const MIN_VISITORS = 5

const visitors = (e: { unique_sessions?: number; event_count?: number }) => e.unique_sessions || e.event_count || 0

/** "/work-with-me" → "Work with me"; "/" → "Homepage". */
export function pageName(path: string): string {
  const clean = path.split("?")[0].replace(/\/?\*$/, "").replace(/\/$/, "")
  if (!clean) return "Homepage"
  const last = clean.split("/").filter(Boolean).pop() ?? clean
  const words = decodeURIComponent(last).replace(/[-_]+/g, " ").trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : clean
}

/** What a person calls a click target: its text or link, not "Link". */
function clickName(c: EventSuggestion): string {
  const raw = GENERIC_TARGET.test(c.target) && c.secondary ? c.secondary : c.target
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw)
      return u.hostname.replace(/^www\./, "")
    } catch {
      return raw
    }
  }
  if (raw.startsWith("/")) return pageName(raw)
  return raw.length > 32 ? `${raw.slice(0, 31)}…` : raw
}

const pageStep = (path: string): SuggestedFunnelStep => ({ name: pageName(path), url: path, type: "page_view" })
const eventStep = (e: EventSuggestion, type: "click" | "form_submission"): SuggestedFunnelStep => ({
  name: type === "click" ? clickName(e) : e.secondary || e.target,
  url: e.target,
  type,
  ...(e.secondary ? { secondary: e.secondary } : {}),
})

export function buildSuggestedFunnels(data: FunnelSuggestionsResponse["data"] | undefined, max = 3): SuggestedFunnel[] {
  if (!data) return []
  // Older responses name the path `page`.
  const pages = (data.pages ?? [])
    .map((p) => ({ ...p, path: p?.path ?? (p as PageSuggestion & { page?: string })?.page ?? "" }))
    .filter((p): p is PageSuggestion => Boolean(p.path) && visitors(p) >= MIN_VISITORS)
    .sort((a, b) => visitors(b) - visitors(a))
  const forms = [...(data.form_submissions ?? [])].filter((f) => f?.target && visitors(f) > 0).sort((a, b) => visitors(b) - visitors(a))
  const clicks = [...(data.clicks ?? [])]
    .filter((c) => c?.target && visitors(c) >= MIN_VISITORS)
    .filter((c) => !CHROME_CLICK.test(c.target) && !(GENERIC_TARGET.test(c.target) && !c.secondary))
    .sort((a, b) => visitors(b) - visitors(a))
  if (pages.length === 0) return []

  const entry = pages.find((p) => p.path === "/") ?? pages[0]
  const intent = pages.find((p) => p.path !== entry.path && INTENT_PAGE.test(p.path))
  const content = pages.find((p) => CONTENT_PAGE.test(p.path))
  const form = forms[0]
  // A click that leads to the entry page itself (logo, "/") is navigation, not intent.
  const cta = clicks.find((c) => c.target !== entry.path && c.secondary !== entry.path)

  const out: SuggestedFunnel[] = []
  const push = (name: string, steps: SuggestedFunnelStep[]) => {
    if (steps.length < 2) return
    const sig = steps.map((s) => `${s.type}:${s.url}:${s.secondary ?? ""}`).join(">")
    if (out.some((f) => f.id === `suggested:${sig}`)) return
    out.push({ id: `suggested:${sig}`, name, steps })
  }

  // 1. Landing to the intent page, then the form when there is one.
  if (intent) {
    push(
      `${pageName(entry.path)} to ${pageName(intent.path).toLowerCase()}`,
      [pageStep(entry.path), pageStep(intent.path), ...(form ? [eventStep(form, "form_submission")] : [])],
    )
  }
  // 2. Landing straight to a form submit.
  if (form) push(`${pageName(entry.path)} to form submit`, [pageStep(entry.path), eventStep(form, "form_submission")])
  // 3. Landing to its most-clicked call to action.
  // Sentence case mid-name: "Homepage to work with me click", not "Work with me".
  if (cta) {
    const name = clickName(cta)
    const mid = /^[A-Z][a-z]/.test(name) ? name.charAt(0).toLowerCase() + name.slice(1) : name
    push(`${pageName(entry.path)} to ${mid} click`, [pageStep(entry.path), eventStep(cta, "click")])
  }
  // 4. Content readers reaching the intent page.
  if (content && intent && content.path !== intent.path) {
    const section = content.path.split("/").filter(Boolean)[0]
    push(`${pageName(`/${section}`)} readers to ${pageName(intent.path).toLowerCase()}`, [
      // "/blog*" covers the index and every post.
      pageStep(`/${section}*`),
      pageStep(intent.path),
    ])
  }

  return out.slice(0, max)
}

export const isSuggestedFunnelId = (id: string) => id.startsWith("suggested:")
